import React from 'react';
import clsx from 'clsx';

import { VALIDATIONS } from '@shared/constants';
import { Dialog, Input, ToastDialog, Grid } from '@shared/components';
import { debounce, writeXLSFile, getJWKS, onTokenUpdate } from '@utils';

import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import LinearProgress from '@material-ui/core/LinearProgress';
import Tooltip from '@material-ui/core/Tooltip';

import LoadingGif from '@assets/icons/spinner-144x144px.gif';
import SuccessIcon from '@assets/icons/done-green.svg';
import ErrorIcon from '@assets/icons/cross-red.svg';
import AddIcon from '@assets/icons/plus.svg';
import AddDisabledIcon from '@assets/icons/add.svg';
import ClearIcon from '@assets/icons/cross-white.svg';
import WarningIcon from '@assets/icons/error.svg';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import { DonutService, RingCentralService } from '@consumers/services';

import FilterComponent from './filter';
import UpdateCallerId from '../update-caller-id';
import useStyles from './style';
import { getShowDialogStatus } from '../../../utils';

const paperElevation = 1;
let accountId = null;
const numericRegExp = /^\d+$/;
const noop = () => {};
let isProgressBarOpen = false;

const EXTENSION_TYPE = {
  LIMITED: 'Limited',
  USER: 'User',
  // VIRTUAL_USER: 'VirtualUser',
  // DIGITAL_USER: 'DigitalUser',
  // FAXUSER: 'FaxUser',
};

const defaultState = {
  uid: '',
  accountName: null,
  tabId: '',
  errorList: {},
  filteredMatchingDIDInfo: [],
  isFetching: false,
  isLoading: false,
  isErrorEncountered: false,
  isUidIncorrect: false,
  showToastDialog: false,
  toastIcon: null,
  toastActions: <></>,
  toastDialogContent: '',
  toastDialogHeading: '',
  isRunningReport: false,
  isExportingReport: false,
  isFiltering: false,
  extensions: [],
  sites: [],
  devices: [],
  deviceList: [],
  phoneNumbers: [],
  selectedFilters: [],
  sheetData: [],
  isDownloadReady: false,
  showProgessBar: false,
  sortObj: null,
  pageSize: 20,
  pageNumber: 1,
  jwksToken: null,
  isDisabled: false,
  extensionsToWorkOn: [],
  isRateLimited: false,
  rateLimitDelay: 0,
  hasRunAudit: false,
  hasNewChanges: false,
};

const CallerId = () => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);

  //dATA FETCHERS
  const fetchSiteList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllSites(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      const sitesArray = [
        {
          key: Math.random(),
          label: 'Main site',
          value: 'Main site',
          isSiteType: true,
        },
      ];

      data.forEach((item, index) => {
        if (item.name === 'Main site') return;
        sitesArray.push({
          key: Math.random(),
          label: item.name,
          value: item.name,
          isSiteType: true,
        });
      });
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        sites: sitesArray,
      }));
    });
  };

  const fetchDeviceList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getAllDevices(
      state.uid,
      state.jwksToken
    );
    handleJwksResponse(response, data => {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        devices: data,
      }));
      createDeviceList(data);
    });
  };

  const createDeviceList = deviceArray => {
    let devices = [];

    for (let device of deviceArray) {
      if (device.extension) {
        devices.push(device);
      }
    }

    setState(prevState => ({
      ...prevState,
      deviceList: devices,
    }));
  };

  const fetchExtensionList = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));

    const query = Object.values(EXTENSION_TYPE).reduce(
      (acc, extension, index) =>
        `${acc}${index > 0 ? '&' : ''}type=${extension}`,
      ''
    );

    const response = await RingCentralService.getAllExtensions(
      state.uid,
      state.jwksToken,
      query
    );
    handleJwksResponse(response, data => {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        extensions: data,
        extensionsToWorkOn: data,
      }));
    });
  };

  const fetchPhoneNumbers = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const response = await RingCentralService.getPhoneNumbers(
      state.uid,
      state.jwksToken,
      'DirectNumber'
    );
    handleJwksResponse(response, data => {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        phoneNumbers: data,
      }));
    });
  };
  //Create the initial array by matching numbers and devices while filtering out numbers without extensions
  const getMatchingDIDInfo = () => {
    if (state.phoneNumbers) {
      let matchingDIDInfo = [];
      for (let num of state.phoneNumbers) {
        if (num.extension) {
          let matchingDevice = state.deviceList.find(device => {
            return device.extension?.id === num.extension?.id;
          });

          matchingDIDInfo.push({
            did: num.phoneNumber,
            numId: num.id,
            extId: num.extension.id,
            deviceId: matchingDevice ? matchingDevice.id : null,
            extensionNum: num.extension.extensionNumber,
          });
        } else {
          matchingDIDInfo.push({
            did: num.phoneNumber,
            numId: num.id,
            extId: null,
            deviceId: null,
            extensionNum: null,
          });
        }
      }
      return matchingDIDInfo;
    }
  };

  //Create Master Array with all the data needed to complete the PUT request
  const createMasterArray = async () => {
    let filteredMatchingDIDInfo = getMatchingDIDInfo();

    setState(prevState => ({
      ...prevState,
      filteredMatchingDIDInfo,
    }));
  };

  const fetchAccountName = async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await DonutService.get(state.uid?.trim());
    if (error) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        showToastDialog: true,
        toastIcon: WarningIcon,
        toastDialogContent: `Alert: ${
          Array.isArray(error) ? error[0].message : error
        }`,
        toastDialogHeading: 'UID Not Found',
        toastActions: (
          <div className='mt-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  toastIcon: defaultState.toastIcon,
                  toastActions: defaultState.toastActions,
                  showToastDialog: defaultState.showToastDialog,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastDialogContent: defaultState.toastDialogContent,
                  isErrorEncountered: defaultState.isErrorEncountered,
                }));
              }}
            >
              Close and Continue
            </Button>
          </div>
        ),
      }));
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        accountName: data?.account?.name,
      }));
    }
  };

  //HANDLERS
  const handleSortChange = React.useCallback((field, order) => {
    let sortObj = order ? { field, order } : null;
    setState(prevState => ({
      ...prevState,
      isFetching: true,
    }));
    debounce(sort =>
      setState(prevState => ({
        ...prevState,
        sortObj: sort,
      }))
    )(sortObj);
  }, []);

  const handleExport = () => {
    let dataToBeWrite = state.sheetData;
    if (!dataToBeWrite.length) {
      return setState(prevState => ({
        ...prevState,
        isErrorEncountered: true,
        showToastDialog: true,
        toastDialogContent: `Error: No results found for filter parameters`,
        toastDialogHeading: 'Report Error',
      }));
    }
    let fileExtension = 'xlsx';
    writeXLSFile(
      `Caller-Id-Audit-${state.uid}.${fileExtension}`,
      'sheet',
      dataToBeWrite,
      {
        bookType: fileExtension,
        raw: false,
      }
    );
  };

  const prepareExtensionBatches = data => {
    let batches = [];
    const DEVICE_BATCH_LIMIT = 15;
    for (
      let batchIndex = 1;
      batchIndex <= Math.ceil(data.length / DEVICE_BATCH_LIMIT);
      batchIndex += 1
    ) {
      batches.push(
        (
          data.slice(
            (batchIndex - 1) * DEVICE_BATCH_LIMIT,
            batchIndex * DEVICE_BATCH_LIMIT
          ) || []
        ).map(row => ({ ...row, batchIndex }))
      );
    }

    return batches;
  };

  const getByFeatureData = (data, name) => {
    const finalValue = data.byFeature
      .filter(data => data.feature === name && !!data.callerId?.phoneInfo)
      .map(data => data.callerId.phoneInfo.phoneNumber);

    return finalValue.toString() || '-';
  };

  const getByDeviceData = data => {
    if (data.byDevice) {
      let tempArr = [];
      data.byDevice.forEach(record => {
        if (record.device === undefined) {
          return;
        }
        if (record.callerId) {
          if (record.callerId.phoneInfo) {
            if (!tempArr.includes(record.callerId.phoneInfo.phoneNumber)) {
              tempArr.push(record.callerId.phoneInfo.phoneNumber);
            }
          }
        }
      });
      return tempArr.join(',');
    } else {
      return '-';
    }
  };
  const downloadExtensionBatches = async batches => {
    let tempSheetData = state.sheetData;
    let rowInfo = {};
    return new Promise(async resolve => {
      const responses = [];
      if (batches && batches.length === 0) {
        resolve(responses);
      }
      for (let index = 0; index < batches.length; index += 1) {
        if (!isProgressBarOpen) {
          setState(prevState => ({
            ...prevState,
            sheetData: [],
            isRunningReport: false,
          }));
          break;
        }
        let record = batches[index];
        let token = null;
        setState(prevState => {
          token = prevState.jwksToken;
          return {
            ...prevState,
          };
        });
        const response = await RingCentralService.getCallerIds(
          record.id,
          state.uid,
          token,
          rateLimit => {
            const { delay } = rateLimit;
            setState(prevState => ({
              ...prevState,
              isRateLimited: true,
              rateLimitDelay: Math.ceil(delay / 1000),
            }));
            const rateLimitDelayInterval = setInterval(() => {
              setState(prevState => {
                const rateLimitDelay =
                  prevState.rateLimitDelay > 0
                    ? prevState.rateLimitDelay - 1
                    : prevState.rateLimitDelay;
                return { ...prevState, rateLimitDelay };
              });
            }, 1000);

            setTimeout(() => {
              clearInterval(rateLimitDelayInterval);
              setState(prevState => ({
                ...prevState,
                isRateLimited: false,
              }));
            }, delay);
          }
        );

        if (response.data) {
          rowInfo = {
            'Extension Id': record.id ? parseInt(record.id) : '-',
            'Extension Number': record?.extensionNumber
              ? parseInt(record?.extensionNumber)
              : '-',
            'Extension Type': record?.type || '-',
            'Extension Name': record?.name || '-',
            'Extension Email': record?.contact?.email || '-',
            'Extension Status': record?.status || '-',
            'Site Name': record?.site?.name || '-',
            'Site Id': record?.site?.id || '-',
            Alternate: getByFeatureData(response.data, 'Alternate'),
            CommonPhone: getByFeatureData(response.data, 'CommonPhone'),
            MobileApp: getByFeatureData(response.data, 'MobileApp'),
            Delegated: getByFeatureData(response.data, 'Delegated'),
            FaxNumber: getByFeatureData(response.data, 'FaxNumber'),
            CallFlip: getByFeatureData(response.data, 'CallFlip'),
            RingOut: getByFeatureData(response.data, 'RingOut'),
            RingMe: getByFeatureData(response.data, 'RingMe'),
            AdditionalSoftphone: getByFeatureData(
              response.data,
              'AdditionalSoftphone'
            ),
            DeviceCallerId: getByDeviceData(response.data),
          };
          tempSheetData.push(rowInfo);
          setState(prevState => ({
            ...prevState,
            sheetData: tempSheetData,
          }));

          responses.push({ success: true, data: response.data });
        } else {
          responses.push({ success: false, error: response.error });
          resolve(responses);
          break;
        }
        if (index === batches.length - 1) {
          resolve(responses);
        }
      }
    });
  };

  const handleAuditExport = async (flag = false) => {
    let extensions = state.extensionsToWorkOn;
    if (!extensions.length) {
      return setState(prevState => ({
        ...prevState,
        isExportingReport: false,
        isErrorEncountered: true,
        showToastDialog: true,
        toastDialogContent: `Error: No results found for filter parameters`,
        toastDialogHeading: 'Report Error',
      }));
    }
    isProgressBarOpen = true;
    setState(prevState => ({
      ...prevState,
      showProgessBar: true,
      isRunningReport: true,
      isDisabled: true,
    }));
    const extensionRequests = prepareExtensionBatches(extensions);
    let responses = [];
    for await (let extension of extensionRequests) {
      const response = await downloadExtensionBatches(extension);

      const err = response.find(item => item.error)?.error;
      if (!isProgressBarOpen) {
        setState(prevState => ({
          ...prevState,
          sheetData: [],
          isRunningReport: false,
        }));
        break;
      }
      if (err) {
        setState(prevState => ({
          ...prevState,
          isDownloadReady: !!state.sheetData.length,
          isRunningReport: false,
          showProgessBar: false,
          isErrorEncountered: true,
          showToastDialog: true,
          toastDialogContent: `Error: ${
            Array.isArray(err) ? err[0].message : err
          }`,
          toastDialogHeading: 'Report Error',
        }));
        if (state.sheetData.length) {
          handleExport();
        }
        break;
      }
      responses.push(response);
    }
    responses = responses.flat().filter(response => response);
    if (flag) {
      setState(prevState => ({
        ...prevState,
        hasRunAudit: true,
        hasNewChanges: false,
      }));
    }
    if (state.sheetData.length == state.extensionsToWorkOn.length) {
      isProgressBarOpen = false;
      setState(prevState => ({
        ...prevState,
        isFetching: true,
        isDownloadReady: true,
        isRunningReport: false,
        showProgessBar: defaultState.showProgessBar,
      }));
      handleExport();
    }
  };

  const handleChange = (event, error) => {
    let field = event.currentTarget.name;
    let value = event.currentTarget.value;
    let fieldError = error;
    if (!numericRegExp.test(value)) {
      fieldError = 'Must be a valid number';
    }
    accountId = null;
    setState(prevState => {
      return {
        ...defaultState,
        isExportingReport: false,
        sheetData: [],
        selectedFilters: [],
        [field]: value,
        errorList: {
          ...prevState.errorList,
          [field]: fieldError,
        },
      };
    });
  };

  const handleChipDelete = chipToDelete => {
    setState(prevState => {
      const updatedFilters = state.selectedFilters.filter(
        chip => chip.label !== chipToDelete.label
      );

      const value =
        updatedFilters.length === 0
          ? state.extensions
          : getData(state.extensions, false, updatedFilters);

      return {
        ...prevState,
        selectedFilters: updatedFilters,
        extensionsToWorkOn: value,
        isDownloadReady: false,
        sheetData: [],
        hasNewChanges: true,
      };
    });
  };

  //UTILS
  const getInputAdorment = () => {
    if (state.isLoading) {
      return <img alt='' src={LoadingGif} width='26' height='26' />;
    } else if (state.isUidIncorrect) {
      return <img alt='' src={ErrorIcon} width='18' height='18' />;
    } else if (!!state.accountName) {
      return <img alt='' src={SuccessIcon} width='26' height='26' />;
    } else {
      return null;
    }
  };

  const getChipLabel = chip => {
    let type = 'Site';
    return `${type}: ${chip.label}`;
  };

  const getData = (
    dataToFilter = [],
    dataToDisplay = false,
    filters = state.selectedFilters,
    pageSize = state.pageSize,
    pageNumber = state.pageNumber,
    sort = state.sortObj
  ) => {
    let preparedData = dataToFilter;
    if (!preparedData || preparedData.length === 0) {
      return [];
    }

    if (filters.length && !dataToDisplay) {
      const siteFilter = filters.map(item => item.value);

      const filteredData = state.selectedFilters.length
        ? state.extensions.filter(
            extension =>
              siteFilter.includes(String(extension?.site?.id)) ||
              siteFilter.includes(String(extension?.site?.name))
          )
        : [...state.extensions];
      preparedData = filteredData;
    }

    if (dataToDisplay) {
      let sortObj = sort ? sort : { field: 'Extension Id', order: 'ASC' };
      preparedData.sort((rowA, rowB) => {
        if (sortObj.order === 'ASC') {
          return rowA[sortObj.field] > rowB[sortObj.field] ? 1 : -1;
        } else {
          return rowB[sortObj.field] > rowA[sortObj.field] ? 1 : -1;
        }
      });
    }

    if (pageSize !== -1) {
      preparedData = preparedData.slice(
        (pageNumber - 1) * pageSize,
        (pageNumber - 1) * pageSize + pageSize
      );
    }

    return preparedData;
  };

  //COLUMNS

  const columns = [
    {
      field: 'Extension Id',
      label: 'EXTENSION ID',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Id'] !== '-' ? row['Extension Id'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Id']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Number',
      label: 'EXTENSION NUMBER',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['Extension Number'] !== '-' ? row['Extension Number'] : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Number']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Type',
      label: 'EXTENSION TYPE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Type'] !== '-' ? row['Extension Type'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Type']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Name',
      label: 'EXTENSION NAME',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Name'] !== '-' ? row['Extension Name'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Name']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Email',
      label: 'EXTENSION EMAIL',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Extension Email'] !== '-' ? row['Extension Email'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Email']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Extension Status',
      label: 'EXTENSION STATUS',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['Extension Status'] !== '-' ? row['Extension Status'] : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Extension Status']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Site Name',
      label: 'SITE EXTENSION',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Site Name'] !== '-' ? row['Site Name'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Site Name']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Site Id',
      label: 'SITE ID',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Site Id'] !== '-' ? row['Site Id'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Site Id']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Alternate',
      label: 'ALTERNATE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Alternate'] !== '-' ? row['Alternate'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Alternate']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'CommonPhone',
      label: 'COMMON PHONE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['CommonPhone'] !== '-' ? row['CommonPhone'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['CommonPhone']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'MobileApp',
      label: 'MOBILE APP',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['MobileApp'] !== '-' ? row['MobileApp'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['MobileApp']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'Delegated',
      label: 'DELEGATED',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['Delegated'] !== '-' ? row['Delegated'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['Delegated']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'FaxNumber',
      label: 'FAX NUMBER',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['FaxNumber'] !== '-' ? row['FaxNumber'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['FaxNumber']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'CallFlip',
      label: 'CALL FLIP',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['CallFlip'] !== '-' ? row['CallFlip'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['CallFlip']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'RingOut',
      label: 'RING OUT',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['RingOut'] !== '-' ? row['RingOut'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['RingOut']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'RingMe',
      label: 'RING ME',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['RingMe'] !== '-' ? row['RingMe'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['RingMe']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'AdditionalSoftphone',
      label: 'ADDITIONAL SOFTPHONE',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={
              row['AdditionalSoftphone'] !== '-'
                ? row['AdditionalSoftphone']
                : ''
            }
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['AdditionalSoftphone']}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'DeviceCallerId',
      label: 'DEVICE CALLER ID(s)',
      canSort: true,
      render: row => {
        return (
          <Tooltip
            title={row['DeviceCallerId'] !== '-' ? row['DeviceCallerId'] : ''}
            placement='top-start'
          >
            <Typography variant='body2' noWrap>
              {row['DeviceCallerId']}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  const fetchJwksToken = async accountUid => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
    }));
    const { data, error } = await getJWKS(accountUid);
    if (error) {
      const isInvalidUid =
        Array.isArray(error) && error[0].errors[0].errorCode === 'OAU-222';
      const errorMessage = isInvalidUid
        ? `Alert: UID not located in Salesforce.`
        : `Error: ${Array.isArray(error) ? error[0].errors[0].message : error}`;

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        isErrorEncountered: true,
        isUidIncorrect: true,
        showToastDialog: true,
        toastIcon: WarningIcon,
        toastDialogContent: errorMessage,
        toastDialogHeading: isInvalidUid ? 'UID Not Found' : 'Report Error...',
        toastActions: (
          <div className='mt-5'>
            <Button
              variant='contained'
              color='primary'
              className='bold-font'
              onClick={() => {
                setState(prevState => ({
                  ...prevState,
                  toastIcon: defaultState.toastIcon,
                  toastActions: defaultState.toastActions,
                  showToastDialog: defaultState.showToastDialog,
                  toastDialogHeading: defaultState.toastDialogHeading,
                  toastDialogContent: defaultState.toastDialogContent,
                  isErrorEncountered: defaultState.isErrorEncountered,
                }));
              }}
            >
              Close and Continue
            </Button>
          </div>
        ),
      }));
    } else {
      if (data) {
        setState(prevState => ({
          ...prevState,
          isLoading: true,
        }));
        onTokenUpdate(async uid => {
          const _accountUid = uid || accountUid;
          const { data, error } = await getJWKS(_accountUid);
          if (!data) {
            setState(prevState => ({
              ...prevState,
              isLoading: false,
            }));
            return false;
          }
          setState(prevState => ({
            ...prevState,
            jwksToken: data,
            isLoading: false,
          }));
          return data;
        });
      }
      setState(prevState => ({
        ...prevState,
        jwksToken: data,
        isLoading: false,
        tabId: '2',
      }));
    }
  };
  const handleJwksResponse = (response = {}, callBack = noop) => {
    const { data, error, paging, token } = response;

    if (error) {
      if (Array.isArray(error) && error[0].errorCode === 'OAU-213') {
        return false;
      }
    } else {
      callBack(data, error, paging);
      if (token) {
        setState(prevState => ({
          ...prevState,
          jwksToken: token,
        }));
      }
    }
  };
  const renderHeading = () => {
    return (
      <div>
        <Typography variant='h5' className='medium-font mb-2'>
          {state.tabId === '1'
            ? 'Update Caller ID'
            : state.tabId === '2'
            ? 'Caller ID Audit Tool'
            : 'Caller ID and Audit Tool'}
        </Typography>
        <Typography variant='caption' className='color-text-secondary'>
          {state.tabId === '2'
            ? 'View all the caller IDs on an account, and download an Excel file so you can filter and manipulate the records.'
            : "This tool lets you view all the caller IDs on an account, edits the caller id's and download an Excel file so you can filter and manipulate the records."}
        </Typography>
      </div>
    );
  };

  React.useEffect(() => {
    if (!!state.jwksToken && !state.devices.length) {
      fetchAccountName();
      fetchSiteList();
      fetchDeviceList();
      fetchExtensionList();
      fetchPhoneNumbers();
    }
  }, [state.jwksToken]);

  React.useEffect(() => {
    if (!!state.extensions.length && !!state.phoneNumbers.length) {
      createMasterArray();
    }
  }, [state.jwksToken, state.extensions, state.phoneNumbers]);

  const handleApplyFilter = selectedFilters => {
    let updateFilters = state.selectedFilters;
    let keys = updateFilters.map(item => item.key);
    selectedFilters.map(filter => {
      if (!keys.includes(filter.key)) {
        updateFilters.push(filter);
      }
    });
    setState(prevState => ({
      ...prevState,
      isFiltering: false,
      selectedFilters: updateFilters,
      extensionsToWorkOn: getData(state.extensions),
      sheetData: [],
      isDownloadReady: false,
      hasNewChanges: true,
    }));
  };

  const handleTabChange = event => {
    const { value } = event.currentTarget;
    isProgressBarOpen = false;
    setState(prevState => ({
      ...prevState,
      tabId: value,
      isDownloadReady: false,
      isExportingReport: false,
      sheetData: [],
      showProgessBar: false,
      isRunningReport: false,
      isFetching: false,
      hasNewChanges: true,
    }));
  };

  React.useEffect(() => {
    setTimeout(() => {
      setState(prevState => ({
        ...prevState,
        isFetching: false,
      }));
    }, 400);
  }, [
    state.pageSize,
    state.pageNumber,
    state.sortObj,
    state.isDownloadReady,
    state.isExportingReport,
    state.tabId,
  ]);

  const gridData = getData(
    state.sheetData,
    true,
    [],
    state.pageSize,
    state.pageNumber,
    state.sortObj
  );

  return (
    <div>
      <Paper className='p-6 mr-10 ml-10' elevation={paperElevation}>
        <div className='d-flex f-justify-between f-align-center'>
          {renderHeading()}
        </div>
        <Typography variant='body1' className='bold-font mt-4'>
          Enter the UID
        </Typography>
        <div className='d-flex f-align-center'>
          <div className='w-25'>
            <Input
              onKeyDown={event => {
                if (parseInt(event.keyCode) === 13) {
                  if (
                    !event.target.value ||
                    accountId === event.target.value ||
                    !!state.errorList?.uid ||
                    state.jwksToken
                  ) {
                    return;
                  }
                  fetchJwksToken(event.target.value);
                  accountId = event.target.value;
                }
              }}
              type='text'
              placeholder='Target Account (UID)'
              name='uid'
              fullWidth
              className='mb-4'
              value={
                !!state.accountName
                  ? `${state.uid} - ${state.accountName}`
                  : `${state.uid}`
              }
              onChange={(event, error) => {
                handleChange(event, error);
              }}
              hasError={!!state.errorList?.uid}
              helperText={state.errorList?.uid}
              onBlur={event => {
                if (
                  !event.currentTarget.value ||
                  accountId === event.currentTarget.value ||
                  !!state.errorList?.uid ||
                  state.jwksToken
                ) {
                  return;
                }
                fetchJwksToken(event.currentTarget.value);
                accountId = event.currentTarget.value;
              }}
              validations={{
                [VALIDATIONS.MAX_LENGTH]: 19,
                [VALIDATIONS.REQUIRED]: true,
              }}
              InputProps={{
                endAdornment: getInputAdorment(),
              }}
              // disabled={state.isDisabled}
            />
          </div>
          {!!state.devices.length && (
            <div className={clsx('d-flex f-align-center')}>
              <RadioGroup
                className='d-flex flex-row f-align-center ml-10'
                value={state.tabId}
                onChange={event => {
                  handleTabChange(event);
                }}
              >
                <FormControlLabel
                  className='mr-10'
                  control={
                    <Radio
                      color='primary'
                      //  disabled={state.isDisabled}
                    />
                  }
                  value='1'
                  label='Update Caller ID'
                />
                <FormControlLabel
                  className='ml-3'
                  control={
                    <Radio
                      color='primary'
                      // disabled={state.isDisabled}
                    />
                  }
                  value='2'
                  label='Caller ID Audit'
                />
              </RadioGroup>
            </div>
          )}
        </div>
      </Paper>
      {!!state.extensions.length && state.tabId === '2' && (
        <Paper
          className={clsx('mr-10 ml-10 p-6 mt-5')}
          elevation={paperElevation}
        >
          <div className='d-flex f-justify-between f-align-center'>
            <div className={classes.filterPaper}>
              <Button
                variant='outlined'
                color='primary'
                className='flex bold-font mr-4'
                classes={{
                  root: classes.filterBtn,
                }}
                onClick={() => {
                  setState(prevState => ({
                    ...prevState,
                    isFiltering: true,
                  }));
                }}
                // disabled={state.sheetData.length}
                startIcon={
                  <img
                    src={state.sheetData.length ? AddDisabledIcon : AddIcon}
                    alt='add-filter'
                    size='5 px'
                  />
                }
              >
                Add Filter
              </Button>
              {state.selectedFilters.map(chip => {
                return (
                  <Chip
                    label={getChipLabel(chip)}
                    color='primary'
                    className={clsx('mr-4 mt-2 mb-2', classes.siteChip)}
                    onDelete={() => handleChipDelete(chip)}
                    size='small'
                    // disabled={state.sheetData.length}
                    deleteIcon={<img src={ClearIcon} alt='add-filter' />}
                  />
                );
              })}
            </div>
            <div className='d-flex'>
              <div>
                <Button
                  className='mr-3 alt-icon-button'
                  variant='contained'
                  color='primary'
                  onClick={() => {
                    if (state.isDownloadReady) {
                      handleExport();
                    } else {
                      setState(prevState => ({
                        ...prevState,
                        isExportingReport: true,
                      }));
                      handleAuditExport(false);
                    }
                  }}
                >
                  <SystemUpdateAltIcon />
                </Button>
              </div>
              <Button
                variant='contained'
                color='primary'
                className={clsx('bold-font ml-4', {
                  [classes.rerunBtn]: state.hasRunAudit,
                })}
                disabled={state.hasRunAudit && !state.hasNewChanges}
                onClick={() => {
                  if (state.isExportingReport) {
                    setState(prevState => ({
                      ...prevState,
                      isFetching: true,
                      isExportingReport: false,
                      hasRunAudit: true,
                      hasNewChanges: false,
                    }));
                  } else {
                    handleAuditExport(true);
                  }
                }}
              >
                {state.hasRunAudit ? 'Rerun Audit' : 'Run Audit'}
              </Button>
            </div>
          </div>
        </Paper>
      )}
      {state.showToastDialog && (
        <ToastDialog
          open={state.showToastDialog}
          heading={state.toastDialogHeading}
          contentText={state.toastDialogContent}
          isError={state.isErrorEncountered}
          toastActions={state.toastActions}
          toastIcon={state.toastIcon}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              showToastDialog: defaultState.showToastDialog,
              toastDialogHeading: defaultState.toastDialogHeading,
              toastDialogContent: defaultState.toastDialogContent,
              isErrorEncountered: defaultState.isErrorEncountered,
              toastActions: defaultState.toastActions,
              toastIcon: defaultState.toastIcon,
            }));
          }}
        />
      )}
      {state.isFiltering && (
        <FilterComponent
          sites={state.sites}
          isOpen={state.isFiltering}
          onSave={handleApplyFilter}
          onClose={() => {
            setState(prevState => ({
              ...prevState,
              isFiltering: false,
            }));
          }}
        />
      )}
      {state.showProgessBar && (
        <Dialog
          disableBackdropClick
          disableEscapeKeyDown
          open={state.showProgessBar}
          classes={{
            paper: classes.progressDialog,
          }}
          onClose={() => {
            isProgressBarOpen = false;

            setState(prevState => ({
              ...prevState,
              showProgessBar: false,
              isExportingReport: false,
              isDisabled: false,
            }));
          }}
        >
          <Dialog.Title hasClose />
          <Dialog.Content>
            <div className='mt-6'>
              <Typography
                variant='h4'
                className='d-flex f-justify-center bold-font'
              >
                {(
                  state.extensionsToWorkOn.length - state.sheetData.length
                ).toString()}
              </Typography>
              <Typography
                variant='body1'
                className='d-flex f-justify-center bold-font mb-10'
              >
                REMAINING
              </Typography>
              <LinearProgress
                className='mt-2'
                variant='determinate'
                value={
                  (state.sheetData.length / state.extensionsToWorkOn.length) *
                  100
                }
              />
              <Typography variant='body2' className='color-text-secondary mt-1'>
                {Math.floor(
                  parseInt(
                    `${(state.sheetData.length /
                      state.extensionsToWorkOn.length) *
                      100}`
                  )
                )}
                %
              </Typography>
              {state.isRateLimited && (
                <div className='d-flex f-align-center f-justify-center mt-4'>
                  <Chip
                    label={`Oops! Rate limit exceeded. Download will resume in ${state.rateLimitDelay ||
                      '60'} seconds.`}
                    color='primary'
                    className='mr-4 mt-2 mb-2 bg-danger color-white'
                  />
                </div>
              )}
            </div>
          </Dialog.Content>
        </Dialog>
      )}
      {state.isDownloadReady && !state.isExportingReport && (
        <Paper
          className={clsx('mr-10 ml-10 p-6 mt-5 mb-8')}
          elevation={paperElevation}
        >
          <Grid
            classes={{
              container: classes.gridPaper,
            }}
            hasLoader
            sortBy={columns.findIndex(
              column => column.field === state.sortObj?.field
            )}
            sortOrder={state.sortObj?.order}
            isLoading={state.isFetching}
            pageSize={state.pageSize}
            pageNumber={state.pageNumber}
            total={state.sheetData.length}
            columns={columns}
            data={gridData}
            hasSelection={false}
            onPageSizeChange={pageSize =>
              setState(prevState => ({
                ...prevState,
                pageSize,
                pageNumber: 1,
                isFetching: true,
              }))
            }
            onPageNumberChange={pageNumber =>
              setState(prevState => ({
                ...prevState,
                pageNumber,
                isFetching: true,
              }))
            }
            onSort={handleSortChange}
            showRowDivider
          />
        </Paper>
      )}
      {state.tabId === '1' && (
        <UpdateCallerId
          uid={state.uid}
          getJWKSToken={() => {
            let token = null;
            setState(prevState => {
              token = prevState.jwksToken;
              return {
                ...prevState,
              };
            });
            return token;
          }}
          filteredMatchingDIDInfo={state.filteredMatchingDIDInfo}
          extensions={state.extensions}
          setIsDisabled={status => {
            setState(prevState => ({
              ...prevState,
              isDisabled: status,
            }));
          }}
        />
      )}
    </div>
  );
};
export default CallerId;
